import ko from "knockout";
import _ from "lodash";
import Promise from "bluebird";
import moment from "moment";
import qualityManagementService from "services/qualityManagementService";
import qualityAuth from "services/qualityAuth";
import errorCallbackFactory from "util/errorCallbackFactory";
import ViewModel from "viewModels/ViewModel";
import qualityBroadcast from "services/qualityBroadcast";
import BroadcastEnum from "viewModels/util/BroadCastEnum";
import PlaybackHeaderView from "viewModels/PlaybackHeaderView";
import RecordingRestoreDetailsView from "./RecordingRestoreDetailsView";
import FileStateEnum from "viewModels/util/FileStateEnum";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";

const MessagesRecordingView = ViewModel.extend(function (
    conversationId,
    recordingModels,
    conversationObservable,
    index = 0
) {
    const self = this;
    const observableRecordingModels = _.map(
        _.filter(recordingModels, { media: "message" }),
        ko.observable
    );

    self.messageHeader = self.subView(
        PlaybackHeaderView,
        ko.i18n.renderString("playback.recording.duration"),
        "message"
    );
    self.voicemail = ko.observable();
    self.isVoicemail = false;

    // <!-- Need to implement some otherwise unused properties for RecordingView interface
    const readOnlyObservableFalse = self.computed(function () {
        return false;
    });
    self.hasAnnotationText = readOnlyObservableFalse;
    self.hasChat = readOnlyObservableFalse;
    self.hasScreenRecording = readOnlyObservableFalse;
    self.isRecordingAvailable = readOnlyObservableFalse;
    self.isAvailableAudioRecordingWithMissingMedia = readOnlyObservableFalse;
    self.recordingRestoreDetails = self.subView(
        RecordingRestoreDetailsView,
        observableRecordingModels[index],
        {
            isMessagesContainer: ko.observable(true),
            header: ko.observable(self.messageHeader),
        }
    );
    // --> end of RecordingView vars

    self.fileStateArchived =
        recordingModels[index].fileState === FileStateEnum.ARCHIVED;
    self.fileStateDeleted =
        recordingModels[index].fileState === FileStateEnum.DELETED;
    self.fileStateRestoring =
        recordingModels[index].fileState === FileStateEnum.RESTORING;
    self.displayed = ko.observable(true);
    self.hasAudio = ko.observable(false);
    self.isRecordingLoading = ko.observable(true);
    self.showLongRunningMessage = ko.observable(false);
    self.recordingRequestError = ko.observable(false);
    self.isDeleted = ko.observable(false);
    self.hasMessages = true;
    self.recording = ko.observable();
    self.messages = ko.observableArray();
    self.updatingRestoreDates = ko.observable(false);
    self.selectedPeriod = ko.observable();
    self.restorePeriod = null;

    self.conversation = ko.isObservable(conversationObservable)
        ? conversationObservable
        : ko.observable(conversationObservable);

    self.recording(recordingModels[index]);

    self.periods = [
        {
            id: 1,
            name: ko.i18n.renderString("playback.archivePeriods.1day"),
        },
        {
            id: 7,
            name: ko.i18n.renderString("playback.archivePeriods.7days"),
        },
        {
            id: 30,
            name: ko.i18n.renderString("playback.archivePeriods.30days"),
        },
    ];

    self.subscribe(self.selectedPeriod, function (newValue) {
        if (newValue && newValue.id != self.restorePeriod) {
            self.restorePeriod = newValue.id;
            self.restoreAllArchivedRecordings(newValue.id);
        }
    });

    self.searchPeriods = function (searchTerm) {
        let filteredPeriods = self.periods;
        if (searchTerm.length > 0) {
            filteredPeriods = _.filter(self.periods, function (period) {
                return (
                    period.name
                        .toLowerCase()
                        .indexOf(searchTerm.toLowerCase()) >= 0
                );
            });
        }
        return Promise.resolve(filteredPeriods);
    };

    self.handleRestorePeriodChange = function (_data, event) {
        const restorePeriod = parseInt(event?.target?.value);
        if (!isNaN(restorePeriod)) {
            self.restoreAllArchivedRecordings(restorePeriod);
        }
    };

    self.orgRestoresRemaining = self.computed(function () {
        if (
            self.recording() &&
            self.recording().remainingRestorationsAllowedForOrg
        ) {
            return ko.unwrap(
                self.recording().remainingRestorationsAllowedForOrg
            );
        }
        return 0;
    });

    self.canSetRestoreDays = self.computed(function () {
        // When feature toggle is enabled, always allow restores regardless of remaining restorations
        return (
            featureToggleService.isEnabled(
                FeatureToggle.WEM_1308_REMOVE_ARCHIVAL_RESTORATIONS_LIMIT
            ) || self.orgRestoresRemaining() > 0
        );
    });

    self.archivedDescriptionKey = self.computed(() => {
        if (
            featureToggleService.isEnabled(
                FeatureToggle.WEM_1308_REMOVE_ARCHIVAL_RESTORATIONS_LIMIT
            )
        ) {
            return "messageDetails.hasArchivedRecordings.description.noRestoresCount";
        }

        return self.canSetRestoreDays()
            ? "messageDetails.hasArchivedRecordings.description"
            : "playback.recordingIsArchived.description.noRestoresAvailable";
    });

    const messageIdToParticipantPurposeMap = self.computed(function () {
        const map = {};
        if (
            !self.conversation() ||
            !self.conversation().participants ||
            !self.conversation().participants()
        ) {
            return map;
        }
        _.each(self.conversation().participants(), function (participant) {
            if (participant.messages && participant.messages.length > 0) {
                _.each(participant.messages, function (participantMessages) {
                    _.each(participantMessages.messages, function (message) {
                        map[message.messageId] = participant.purpose;
                    });
                });
            }
        });
        return map;
    });

    function getParticipantPurposeForMessageId(messageId) {
        return messageIdToParticipantPurposeMap()[messageId];
    }

    self.canRestoreMessages = qualityAuth.canRestoreRecording();

    self.archivedRecordings = self.computed(function () {
        return _.filter(observableRecordingModels, function (observableModel) {
            const model = ko.unwrap(observableModel);
            return (
                model.fileState &&
                ko.unwrap(model.fileState) === FileStateEnum.ARCHIVED
            );
        });
    });

    self.restoringRecordings = self.computed(function () {
        return _.filter(observableRecordingModels, function (observableModel) {
            const model = ko.unwrap(observableModel);
            return (
                model.fileState &&
                ko.unwrap(model.fileState) === FileStateEnum.RESTORING
            );
        });
    });

    self.deletedRecordings = self.computed(function () {
        return _.filter(observableRecordingModels, function (observableModel) {
            const model = ko.unwrap(observableModel);
            return (
                model.fileState &&
                ko.unwrap(model.fileState) === FileStateEnum.DELETED
            );
        });
    });

    self.restoreAllArchivedRecordings = function (restorePeriod) {
        if (self.updatingRestoreDates()) {
            return;
        }

        const archivedRecordings = self.archivedRecordings();
        const restorePromises = _.map(
            archivedRecordings,
            function (observableRecording) {
                const recording = ko.unwrap(observableRecording);
                const opts = {};

                recording.archiveDate = moment()
                    .add(restorePeriod, "day")
                    .toISOString();

                self.updatingRestoreDates(true);
                recording.fileState = "RESTORING";
                return qualityManagementService
                    .updateRecording(recording, opts)
                    .then(function (result) {
                        observableRecording(
                            _.assign(recording, ko.toJS(result))
                        );
                    });
            }
        );

        Promise.all(restorePromises)
            .catch(
                errorCallbackFactory.createCallback({
                    namespace: "playback.archive.restoreRequest",
                })
            )
            .finally(function () {
                self.updatingRestoreDates(false);
            });
    };

    self.getContentLengthLabelText = function (contentLength) {
        if (isNaN(contentLength)) {
            return "";
        }

        let sizeLabelKey = "messageDetails.sizeInBytes";
        let size = contentLength;
        if (size > 1000) {
            size = size / 1000;
            sizeLabelKey = "messageDetails.sizeInKiloBytes";
        }
        if (size > 1000) {
            size = size / 1000;
            sizeLabelKey = "messageDetails.sizeInMegaBytes";
        }
        // Coerce to number to drop trailing 0s
        size = Number(size.toFixed(2));
        return ko.i18n.renderString(sizeLabelKey, { size: size });
    };

    self.getMediaTypeIconClass = function (mediaType) {
        let mediaTypeIconClass = "fa fa-2x fa-file-text-o";
        if (mediaType.startsWith) {
            if (mediaType.startsWith("video/")) {
                mediaTypeIconClass = "fa fa-2x pc-icon-video-alpha";
            }
        }
        return mediaTypeIconClass;
    };

    const collatedMessages = _.flatMap(
        recordingModels,
        function (messageModel) {
            return _.map(
                messageModel.messagingTranscript,
                function (transcript) {
                    const clone = _.cloneDeep(transcript);
                    clone.timestamp = new Date(transcript.timestamp);
                    clone.userImages = ko.observable();

                    clone.participantPurpose =
                        getParticipantPurposeForMessageId(clone.id);
                    clone.isBotParticipant = false;

                    if (clone.fromUser && clone.fromUser.name) {
                        clone.username = clone.fromUser.name;
                    } else if (clone.participantPurpose === "workflow") {
                        clone.username = ko.i18n.renderString(
                            "messageDetails.workflowParticipant"
                        );
                        clone.isBotParticipant = true;
                    } else if (clone.participantPurpose === "api") {
                        clone.username = ko.i18n.renderString(
                            "messageDetails.apiParticipant"
                        );
                        clone.isBotParticipant = true;
                    } else if (clone.participantPurpose === "campaign") {
                        clone.username = ko.i18n.renderString(
                            "messageDetails.campaignParticipant"
                        );
                        clone.isBotParticipant = true;
                    } else if (clone.participantPurpose === "acd") {
                        clone.username = ko.i18n.renderString(
                            "messageDetails.acdParticipant"
                        );
                        clone.isBotParticipant = true;
                    }

                    clone.participantStyle = ko.computed(function () {
                        // Older SMS recordings don't have messageIds on the transcripts, so
                        // we can't look up the participants. For those, it is safe to assume the
                        // first one is an inbound customer, and it is not a workflow participant.
                        // Such recordings predates outbound messages and message workflows.
                        let isInternalParticipant =
                            self.messages().length &&
                            clone.to !== self.messages()[0].to;
                        if (clone.participantPurpose) {
                            isInternalParticipant =
                                clone.participantPurpose === "agent" ||
                                clone.participantPurpose === "workflow" ||
                                clone.participantPurpose === "api" ||
                                clone.participantPurpose === "campaign" ||
                                clone.participantPurpose === "acd" ||
                                clone.participantPurpose === "user";
                        }

                        return isInternalParticipant ? "agent" : "customer";
                    });

                    if (clone.messageMediaAttachments) {
                        clone.messageMediaAttachments = _.map(
                            clone.messageMediaAttachments,
                            function (attachment) {
                                attachment.expandAttachment =
                                    ko.observable(true);
                                attachment.hasRenderError =
                                    ko.observable(false);
                                attachment.caretStyle =
                                    ko.observable("fa-caret-down");
                                attachment.toggleAttachmentVisibility =
                                    function () {
                                        attachment.expandAttachment(
                                            !attachment.expandAttachment()
                                        );
                                        attachment.caretStyle(
                                            attachment.expandAttachment()
                                                ? "fa-caret-down"
                                                : "fa-caret-right"
                                        );
                                    };

                                attachment.sizeLabel =
                                    self.getContentLengthLabelText(
                                        attachment.contentLength
                                    );
                                attachment.mediaTypeIconClass =
                                    self.getMediaTypeIconClass(
                                        attachment.mediaType
                                    );

                                attachment.onRenderError = function (error) {
                                    attachment.hasRenderError(true);
                                };
                                return attachment;
                            }
                        );
                    }

                    return clone;
                }
            );
        }
    );

    // set the header and duration when messages is not empty
    self.subscribe(self.messages, function (messages) {
        if (messages.length > 0) {
            if (
                self.conversation() &&
                self.conversation().startTime &&
                self.conversation().endTime
            ) {
                self.messageHeader.setHeader(
                    self.conversation().startTime(),
                    self.conversation().endTime()
                );
            } else if (
                self.recording() &&
                self.recording().startTime &&
                self.recording().endTime
            ) {
                self.messageHeader.setHeader(
                    self.recording().startTime,
                    self.recording().endTime
                );
            }
        }
        // set the header when messages is archived
        else if (
            self.recording() &&
            self.recording().fileState === FileStateEnum.ARCHIVED
        ) {
            if (
                self.conversation() &&
                self.conversation().startTime &&
                self.conversation().endTime
            ) {
                self.messageHeader.setStartEndTime(
                    self.conversation().startTime(),
                    self.conversation().endTime()
                );
            } else if (
                self.recording() &&
                self.recording().startTime &&
                self.recording().endTime
            ) {
                self.messageHeader.setStartEndTime(
                    self.recording().startTime,
                    self.recording().endTime
                );
            }
        }
    });
    self.messages(
        _.sortBy(collatedMessages, function (messageModel) {
            return messageModel.timestamp;
        })
    );

    const userImages = {};
    qualityBroadcast.subscribe(
        conversationId,
        BroadcastEnum.USER_IMAGES_AVAILABLE,
        function (message) {
            _.assign(userImages, message);
            _.each(self.messages(), function (message) {
                if (message.fromUser && message.fromUser.id) {
                    const images = userImages[message.fromUser.id];
                    message.userImages(images);
                }
            });
        }
    );
});

export default MessagesRecordingView;
